import { extendObservable, computed } from 'mobx'
import moment from 'moment'
import xorBy from 'lodash/xorBy'
import intersection from 'lodash/intersection'
import fromPairs from 'lodash/fromPairs'
import omit from 'lodash/omit'
import queryString from 'query-string'

import { stringifyDate, downloadFromUrl, exportDialogMessage, tokenStorage } from '@atheneum/api/dist/lib'

import { expertApi, expertPaymentApi, feeApi, reportApi } from '@atheneum/api'

export default class ExpertPaymentsStore {
  constructor (staticData, growlStore) {
    extendObservable(this, {
      loading: false,
      expertPayments: [],
      selectedPayments: [],
      selectedStatus: null,
      paymentStatusTransitions: computed(this.getPaymentStatusTransitions),
      count: null,
      query: this.getDefaultQuery(),
      sidebarData: {
        loading: false,
        epdData: null,
        cpdData: null,
        feeData: null
      },
      staticData,
      growlStore
    })
  }

  getDefaultQuery = () => {
    return {
      fromDate: moment()
        .startOf('month')
        .toDate(),
      toDate: moment().toDate(),
      cpdPaymentStatusIds: [],
      atheneumOfficeId: null,
      invoiced: false,
      searchString: '',
      sortOptions: [],
      invoiceNumbers: null,
      limit: 100,
      page: 1
    }
  }

  prepareQuery = () => {
    const query = {
      ...this.query,
      fromDate: stringifyDate(this.query.fromDate),
      toDate: stringifyDate(this.query.toDate),
      cpdPaymentStatusIds: this.query.cpdPaymentStatusIds.slice(),
      offset: (this.query.page - 1) * this.query.limit,
      sortOptions: fromPairs(this.query.sortOptions)
    }

    return omit(query, 'page')
  }

  search = async (query = {}) => {
    Object.assign(this.query, query)

    this.loading = true

    try {
      this.selectedPayments.replace([])
      this.selectedStatus = null

      const { rows, count } = await expertPaymentApi.searchExpertPayments(this.prepareQuery())

      this.expertPayments.replace(rows)
      this.count = count
    } catch (error) {
      this.growlStore.showError(error)
    } finally {
      this.loading = false
    }
  }

  changePage = page => {
    this.search({ page })
  }

  export = () => {
    const path = '/api/report/expert-payment/export'
    const token = tokenStorage.get()
    const query = omit(this.prepareQuery(), 'sortOptions')
    const queryParams = queryString.stringify({ ...query, token })

    window.open(`${path}?${queryParams}`)
  }

  expertPaymentExport = async props => {
    if (this.exportInProgress) return

    const token = tokenStorage.get()
    const query = omit(this.prepareQuery(), ['sortOptions', 'invoiceNumbers'])
    const queryParams = queryString.stringify({ ...query, token })

    const filename = `expert_payment_report_${moment(query.fromDate).format(
      'YYYY-MM-DD'
    )}-${moment(query.toDate).format('YYYY-MM-DD')}.xlsx`

    const { jobId } = await reportApi.exportExpertPayment(queryParams).catch(() =>
      alert('export error')
    )

    props.queueProcessorStore.trackJob(jobId, ({ url }) =>
      downloadFromUrl({ url: `${url}?token=${tokenStorage.get()}`, filename })
    )

    this.job = props.queueProcessorStore.jobs[jobId]

    if (this.job && this.job.loading) {
      this.exportInProgress = true
      setTimeout(() => {
        this.exportInProgress = false
      }, 1000)
    }

    await exportDialogMessage({ exportType: 'expert payment report' })
  }

  toggleSortOption = field => {
    const existing = this.query.sortOptions.find(([f]) => f === field)
    const newSortOptions = this.query.sortOptions.filter(([f]) => f !== field)

    if (!existing) {
      newSortOptions.unshift([field, 'asc'])
    } else if (existing && existing[1] === 'asc') {
      newSortOptions.unshift([field, 'desc'])
    }

    this.query.sortOptions.replace(newSortOptions)

    this.search()
  }

  togglePayment = payment => {
    this.selectedPayments.replace(
      xorBy(this.selectedPayments, [payment], 'cpdId')
    )

    if (!this.paymentStatusTransitions.includes(this.selectedStatus)) {
      this.selectedStatus = null
    }
  }

  toggleAllPayments = checked => {
    if (!checked) {
      this.selectedPayments.replace([])
    } else {
      this.selectedPayments.replace(this.expertPayments)
    }
  }

  batchUpdatePaymentStatus = async () => {
    try {
      await expertPaymentApi.batchUpdatePaymentStatus(
        this.selectedPayments.map(({ cpdId }) => cpdId),
        this.selectedStatus
      )
      this.search()
      this.growlStore.showSuccess('Payment statuses updated successfully')
    } catch (error) {
      this.growlStore.showError(error)
    }
  }

  getPaymentStatusTransitions = () => {
    const {
      NO_EPD_RECEIVED,
      COLLECTING_PAYMENT_DETAILS,
      REVIEWING_PAYMENT_DETAILS,
      PROCESSING_PAYMENT,
      PAID,
      PAYMENT_TO_EXPERT_NOT_REQUIRED
    } = this.staticData.CONST.PAYMENT_STATUSES

    const transitionMap = {
      [NO_EPD_RECEIVED]: [
        COLLECTING_PAYMENT_DETAILS,
        PAYMENT_TO_EXPERT_NOT_REQUIRED
      ],
      [COLLECTING_PAYMENT_DETAILS]: [
        PROCESSING_PAYMENT,
        PAYMENT_TO_EXPERT_NOT_REQUIRED
      ],
      [REVIEWING_PAYMENT_DETAILS]: [COLLECTING_PAYMENT_DETAILS],
      [PROCESSING_PAYMENT]: [
        PAID,
        COLLECTING_PAYMENT_DETAILS,
        PAYMENT_TO_EXPERT_NOT_REQUIRED
      ],
      [PAID]: [PROCESSING_PAYMENT],
      [PAYMENT_TO_EXPERT_NOT_REQUIRED]: [COLLECTING_PAYMENT_DETAILS]
    }

    const statuses = this.selectedPayments.map(
      ({ paymentStatusId }) => paymentStatusId
    )
    const transitions = statuses.map(status => transitionMap[status])

    return intersection(...transitions)
  }

  loadSidebarData = async ({ expertId, cpdId, feeId }) => {
    try {
      this.sidebarData.loading = true
      const [epdData, cpdData, feeData] = await Promise.all([
        expertApi.getPaymentDetails(expertId),
        expertPaymentApi.getCpd(cpdId),
        feeApi.getFee(feeId)
      ])

      this.sidebarData.epdData = epdData
      this.sidebarData.cpdData = cpdData
      this.sidebarData.feeData = feeData
      this.sidebarData.loading = false
    } catch (error) {
      this.growlStore.showError(error)
    }
  }

  exportInvoice = cpdId => {
    const path = `/api/consultation-payment-detail/${cpdId}/invoice-export`

    window.open(`${path}?token=${tokenStorage.get()}`)
  }

  requestEpdConfirmation = async () => {
    const { epdData, cpdData, feeData } = this.sidebarData
    try {
      await expertPaymentApi.requestEpdConfirmation(epdData, feeData.id)
      this.growlStore.showSuccess('EPD confirmation request sent successfully')
      this.loadSidebarData({
        expertId: epdData.expertId,
        cpdId: cpdData.id,
        feeId: feeData.id
      })
    } catch (error) {
      console.error(error)
    }
  }
}
